/**
 * Hook for fetching smart task intelligence from /crm/intelligence/tasks.
 * Returns open (suggested), resolved, and escalated tasks from the smart task engine.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type {
  CrmActionExecutionPlan,
  CrmActionObservedOutcome,
  CrmActionOutcomeScoring,
  CrmActionReadiness,
  CrmEventActionIntent,
} from "./use-crm-event-action-score";
import type { TaskOperatorReadModel } from "./use-tasks";

export interface IntelligenceTaskMetadata {
  recommendationId?: string;
  intent?: CrmEventActionIntent;
  actionRef?: string;
  confidence?: number;
  executionMode?: "suggest_only";
  readiness?: CrmActionReadiness;
  executionPlan?: CrmActionExecutionPlan;
  observedOutcome?: CrmActionObservedOutcome | null;
  outcomeScoring?: CrmActionOutcomeScoring;
  sourceEventId?: string;
  sourceEventType?: string;
  signalFamily?: string;
  suggestedInput?: Record<string, unknown>;
  scoreDelta?: number;
  scoreExplanation?: string;
}

export interface IntelligenceTask {
  id: string;
  title: string;
  description: string | null;
  type: string;
  priority: string;
  status: string;
  personId: string | null;
  dealId: string | null;
  companyId?: string | null;
  offerId?: string | null;
  assignedTo: string | null;
  dueAt: string | null;
  completedAt?: string | null;
  completedBy?: string | null;
  source?: string | null;
  sourceRuleId?: string | null;
  template?: string | null;
  notes?: string | null;
  outcome?: string | null;
  outcomeNotes?: string | null;
  nextAction?: string | null;
  followUpDate?: string | null;
  contextSnapshot?: Record<string, unknown>;
  scriptTemplate?: string | null;
  triggerEventType: string | null;
  triggerType?: string | null;
  resolutionEventType: string | null;
  metadata?: IntelligenceTaskMetadata | null;
  operatorReadModel?: TaskOperatorReadModel;
  createdAt: string;
  updatedAt?: string;
}

interface IntelligenceTasksData {
  open: IntelligenceTask[];
  resolved: IntelligenceTask[];
  escalated: IntelligenceTask[];
}

export interface ReviewActionRecommendationInput {
  contactId?: string | null;
  targetEntityType?: "person" | "deal" | "company" | "task" | "activity";
  targetEntityId?: string | null;
  recommendationId: string;
  actionRef: string;
  intent: CrmEventActionIntent | string;
  decision: "accepted" | "dismissed";
  priority?: string;
  reason?: string;
  requiresHumanReview?: boolean;
  readinessStatus?: string;
  executionPlanStatus?: string;
  executionPlanMode?: string;
  executionPlanReason?: string;
  confidence?: number;
  sourceEventId?: string;
  sourceEventType?: string;
  signalFamily?: string;
  sourceSurface?: string;
  outcomeScoring?: CrmActionOutcomeScoring;
  suggestedInput?: Record<string, unknown>;
}

export async function reviewCrmActionRecommendation(
  input: ReviewActionRecommendationInput,
): Promise<{ recommendationId: string; decision: "accepted" | "dismissed"; eventId: string; targetEntityType?: string; targetEntityId?: string }> {
  const response = await apiFetch<{
    data: { recommendationId: string; decision: "accepted" | "dismissed"; eventId: string; targetEntityType?: string; targetEntityId?: string };
  }>(
    `/crm/intelligence/action-recommendations/${encodeURIComponent(input.recommendationId)}/review`,
    {
      method: "POST",
      body: JSON.stringify({
        contactId: input.contactId,
        targetEntityType: input.targetEntityType,
        targetEntityId: input.targetEntityId,
        actionRef: input.actionRef,
        intent: input.intent,
        decision: input.decision,
        priority: input.priority,
        reason: input.reason,
        requiresHumanReview: input.requiresHumanReview,
        readinessStatus: input.readinessStatus,
        executionPlanStatus: input.executionPlanStatus,
        executionPlanMode: input.executionPlanMode,
        executionPlanReason: input.executionPlanReason,
        confidence: input.confidence,
        sourceEventId: input.sourceEventId,
        sourceEventType: input.sourceEventType,
        signalFamily: input.signalFamily,
        sourceSurface: input.sourceSurface,
        outcomeScoring: input.outcomeScoring,
        suggestedInput: input.suggestedInput,
      }),
    },
  );

  return response.data;
}

export function useTaskIntelligence() {
  const [data, setData] = useState<IntelligenceTasksData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: IntelligenceTasksData }>(
        "/crm/intelligence/tasks",
      );
      setData(res.data);
    } catch {
      setError("Failed to load suggested tasks");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  return { data, isLoading, error, refetch: fetchTasks, reviewActionRecommendation: reviewCrmActionRecommendation };
}
